import React, { useMemo, useRef } from 'react';
import { Opportunity as OpportunityConst } from '@axiom/const';
import {
  AsyncConnector,
  Button,
  Dropdown,
  Form,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
} from '@axiom/ui';
import type { Candidate, Opportunity } from '@axiom/validation';
import { AhqFindOpportunitySchema } from '@axiom/validation';

import type { ReadOpportunitiesResponseType } from '../../api/opportunity-api';
import { OpportunityApi } from '../../api/opportunity-api';
import { AddCandidatesToOpportunityUtil } from '../../utils/AddCandidatesToOpportunityUtil';

export type FindOpportunityForCandidateModalProps = {
  name?: string;
  onClose: () => void;
  onSuccess?: () => void;
  candidateIds: Array<Candidate['id']>;
  candidateName?: string | null;
  reloadCandidateId?: Candidate['id'];
};

export const FindOpportunityForCandidateModal = ({
  onClose,
  onSuccess,
  candidateIds,
  candidateName,
  reloadCandidateId,
}: FindOpportunityForCandidateModalProps) => {
  const parsedOpportunitiesRef = useRef<Array<{ id: string; name: string }>>(
    []
  );

  const opportunitiesConnector = useMemo(
    () =>
      new AsyncConnector<ReadOpportunitiesResponseType>({
        getData: args => {
          return OpportunityApi.readOpportunities({
            filters: {
              stageCode: [
                OpportunityConst.STAGE_CODE_MAP[OpportunityConst.DEVELOP],
                OpportunityConst.STAGE_CODE_MAP[OpportunityConst.CRAFT],
                OpportunityConst.STAGE_CODE_MAP[OpportunityConst.COMPETE],
                OpportunityConst.STAGE_CODE_MAP[OpportunityConst.CONTRACT],
              ],
              isFulfillmentActive: true,
            },
            search: args.search,
            sort: 'relevance',
            page: args.page,
          });
        },
        parseData: results => {
          const opportunities = results.data.map((opp: Opportunity) => ({
            id: opp.id,
            name: opp.jobName ?? '',
          }));
          parsedOpportunitiesRef.current = opportunities;

          return {
            currentPage: results.meta.currentPage,
            totalPages: results.meta.pageCount,
            data: opportunities,
          };
        },
      }),
    []
  );

  return (
    <Form
      name="FIND_OPPORTUNITY_FOR_CANDIDATE_FORM"
      schema={AhqFindOpportunitySchema}
      initialValues={{
        opportunityId: undefined,
      }}
      onSubmit={async formData => {
        const { opportunityId } = formData;
        if (opportunityId) {
          const opportunityName = parsedOpportunitiesRef.current.find(
            o => o.id === opportunityId
          )?.name;
          const success = await AddCandidatesToOpportunityUtil.submit({
            opportunityId,
            opportunityName,
            candidateIds,
            candidateName,
            reloadCandidateId,
          });
          if (!success) return;
          onSuccess?.();
          onClose();
        }
      }}
    >
      {({ fireSubmit }) => (
        <Modal name="FIND_OPPORTUNITY_FOR_CANDIDATE_MODAL">
          <ModalHeader
            name="FIND_OPPORTUNITY_FOR_CANDIDATE_MODAL_HEADER"
            onClose={() => {
              onClose();
            }}
          >
            Search Opportunities
          </ModalHeader>
          <ModalSection
            name="FIND_OPPORTUNITY_FOR_CANDIDATE_MODAL_SECTION"
            disableScrolling
          >
            <Dropdown
              name="opportunityId"
              options={opportunitiesConnector}
              displayKey="name"
              valueKey="id"
              placeholder="Type to start searching..."
            />
          </ModalSection>
          <ModalFooter name="FIND_OPPORTUNITY_FOR_CANDIDATE_MODAL_FOOTER">
            <Button
              onClick={() => {
                onClose();
              }}
              name="CANCEL"
              variation="outline"
            >
              Cancel
            </Button>
            <Button type="submit" onClick={fireSubmit} name="ADD">
              Add
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
